import React, { useEffect, useState } from "react";
import { executeQuery, executeMutation } from "../../../graphql/client";
import { capture } from "../../../analytics";

// One-off programs card (#289 phase 1). Lists whatever the admin
// Programs tab has put on sale — league registration, tourney-night
// entry — with the caller's price (member vs guest by standing) and a
// Pay button that opens one-time Stripe Checkout. Renders nothing when
// no program is active, so the dashboard is unchanged until David
// configures one. After Stripe bounces back with ?programpaid=<id>
// the card shows a one-time confirmation; the payment itself is
// recorded server-side by the webhook, not by this page.

const PROGRAMS_QUERY = `
  query ProgramsForDashboard {
    programs {
      id
      name
      description
      yourTier
      yourPriceCents
      memberPriceCents
      guestPriceCents
      currency
      yourPaidCount
      yourLastPaidAt
    }
  }
`;

const CHECKOUT_MUTATION = `
  mutation CreateProgramCheckout($programId: ID!, $returnUrl: String!) {
    createProgramCheckoutSession(programId: $programId, returnUrl: $returnUrl)
  }
`;

export const fmtMoney = (cents, currency) => {
  if (cents == null) return "";
  const amt = (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
  return `$${amt}${currency && currency !== "usd" ? " " + currency.toUpperCase() : ""}`;
};

const fmtDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// What the price line says for one program, given the caller's tier.
export const priceLine = (p) => {
  const yours = fmtMoney(p.yourPriceCents, p.currency);
  const member = fmtMoney(p.memberPriceCents, p.currency);
  const guest = fmtMoney(p.guestPriceCents, p.currency);
  if (!yours) return "Price shown at checkout";
  if (p.yourTier === "member") {
    return guest && guest !== yours
      ? `${yours} · member rate (non-members pay ${guest})`
      : `${yours} · member rate`;
  }
  return member && member !== yours
    ? `${yours} · members pay ${member}`
    : yours;
};

// Pull ?programpaid= / ?programcancel= off the URL (once) so a refresh
// doesn't re-celebrate. Returns { paid, cancelled } program ids.
const readReturnParams = () => {
  if (typeof window === "undefined") return { paid: "", cancelled: "" };
  const params = new URLSearchParams(window.location.search);
  const paid = params.get("programpaid") || "";
  const cancelled = params.get("programcancel") || "";
  if (paid || cancelled) {
    params.delete("programpaid");
    params.delete("programcancel");
    const rest = params.toString();
    const url =
      window.location.pathname + (rest ? `?${rest}` : "") + window.location.hash;
    window.history.replaceState({}, "", url);
  }
  return { paid, cancelled };
};

const ProgramsCard = () => {
  const [programs, setPrograms] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [returned, setReturned] = useState({ paid: "", cancelled: "" });

  useEffect(() => {
    setReturned(readReturnParams());
    let cancelled = false;
    (async () => {
      try {
        const out = await executeQuery(PROGRAMS_QUERY);
        if (!cancelled) setPrograms(out?.programs || []);
      } catch (err) {
        // Nothing to show is the safe failure mode for a sales card.
        if (!cancelled) setPrograms([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pay = async (p) => {
    setBusy(p.id);
    setError("");
    capture("program_checkout_started", { programId: p.id, tier: p.yourTier });
    const returnUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}${window.location.pathname}`
        : "https://activebeaches.com/dashboard";
    try {
      const out = await executeMutation(CHECKOUT_MUTATION, {
        programId: p.id,
        returnUrl,
      });
      const url = out?.createProgramCheckoutSession;
      if (url) {
        window.location.href = url;
      } else {
        setError("Stripe returned no checkout URL.");
        setBusy("");
      }
    } catch (err) {
      setError(err.message || "Couldn't start checkout.");
      setBusy("");
    }
  };

  const paidProgram =
    returned.paid && programs
      ? programs.find((p) => p.id === returned.paid)
      : null;
  const showPaidNote = !!returned.paid;

  if (!programs || (programs.length === 0 && !showPaidNote)) return null;

  return (
    <div
      style={{
        background: "#fff8ec",
        border: "1px solid #f0d9a8",
        borderRadius: 10,
        padding: "14px 16px",
        marginBottom: 16,
      }}
    >
      <div style={{ fontWeight: 700, color: "#6b4a06", marginBottom: 4 }}>
        <span role="img" aria-label="receipt">
          🧾
        </span>{" "}
        Register &amp; pay
      </div>
      {showPaidNote && (
        <div
          style={{
            background: "#eef7ee",
            border: "1px solid #b9dcb5",
            color: "#1f4f1d",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 13.5,
            marginBottom: 10,
          }}
        >
          <span role="img" aria-label="party">
            🎉
          </span>{" "}
          <strong>Payment received</strong>
          {paidProgram ? ` for ${paidProgram.name}` : ""}. You're on the list —
          Stripe emails your receipt. (It can take a few seconds to show
          below.)
        </div>
      )}
      {returned.cancelled && !showPaidNote && (
        <div style={{ fontSize: 13, color: "#6a7385", marginBottom: 8 }}>
          Checkout cancelled — nothing was charged.
        </div>
      )}
      {error && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #e7b3ba",
            color: "#b00020",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 13,
            marginBottom: 8,
          }}
        >
          {error}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {programs.map((p) => (
          <div
            key={p.id}
            style={{
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <div style={{ minWidth: 0, flex: "1 1 220px" }}>
              <div style={{ fontWeight: 600, color: "#2b3445", fontSize: 14.5 }}>
                {p.name}
              </div>
              {p.description && (
                <div style={{ fontSize: 13, color: "#4a5263", marginTop: 2 }}>
                  {p.description}
                </div>
              )}
              <div style={{ fontSize: 13, color: "#6b4a06", marginTop: 4 }}>
                {priceLine(p)}
                {p.yourTier === "guest" &&
                  p.memberPriceCents != null &&
                  p.guestPriceCents != null &&
                  p.memberPriceCents < p.guestPriceCents && (
                    <span style={{ color: "#6a7385" }}>
                      {" "}
                      — subscribe above and the member rate applies.
                    </span>
                  )}
              </div>
              {p.yourPaidCount > 0 && (
                <div style={{ fontSize: 12.5, color: "#1f7a3a", marginTop: 3 }}>
                  ✓ You've paid for this {p.yourPaidCount === 1 ? "once" : `${p.yourPaidCount}×`}
                  {p.yourLastPaidAt ? ` (last on ${fmtDate(p.yourLastPaidAt)})` : ""}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => pay(p)}
              disabled={busy === p.id}
              style={{
                background: "#0e7c9b",
                color: "#fff",
                border: "none",
                borderRadius: 999,
                padding: "8px 16px",
                fontWeight: 700,
                fontSize: 14,
                cursor: busy === p.id ? "wait" : "pointer",
                opacity: busy === p.id ? 0.7 : 1,
                flex: "0 0 auto",
              }}
            >
              {busy === p.id
                ? "Opening checkout…"
                : `Pay${p.yourPriceCents != null ? " " + fmtMoney(p.yourPriceCents, p.currency) : ""}`}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProgramsCard;
